import {
  BaseSDK,
  BaseSDKConfig,
  BaseSDKHooks,
  createMessage,
  isHandshakeMessage,
  type Message,
  MessageType,
  type ProgressPayload,
  type PurchaseRequestPayload,
  type PurchaseResultPayload,
  SDKHookFunction,
  SDKStateData,
  SetAdFreePayload,
  type SetLocalePayload,
  type SetMutePayload,
  SyncPayload,
} from '@/core';

export interface HostSDKConfig extends BaseSDKConfig {
  iframe: HTMLIFrameElement;
  allowedOrigins: string[];
  targetOrigin: string;
  initialState?: Partial<SDKStateData>;
}

export interface HostSDKHooks extends BaseSDKHooks {
  onFramesStarted?: SDKHookFunction<[], void>;
  onGameReady?: SDKHookFunction<[], void>;
  onProgressSync?: SDKHookFunction<[snapshot: Record<string, unknown>], void>;
  onProgressRequest?: SDKHookFunction<[], Record<string, unknown> | Promise<Record<string, unknown>>>;
  onPurchaseRequest?: SDKHookFunction<[request: PurchaseRequestPayload], Promise<PurchaseResultPayload>>;
}

export class HostSDK extends BaseSDK<HostSDKConfig, HostSDKHooks> {
  public name = 'HostSDK';

  private processedPurchases = new Set<string>();

  public constructor(config: HostSDKConfig, hooks: HostSDKHooks = {}) {
    if (!config.iframe.contentWindow) {
      throw new Error('iframe.contentWindow is not available');
    }

    super(config, config.iframe.contentWindow, hooks, 'host');

    if (typeof config.initialState === 'object' && config.initialState !== null) {
      Object.entries(config.initialState).forEach(([key, value]) => {
        this.state.set(key as keyof SDKStateData, value);
      });
    }
  }

  public setMuted(muted: boolean): void {
    this.state.set('muted', muted);
    const message = createMessage<SetMutePayload>(MessageType.SET_MUTE, { muted });
    this.sendOrQueue(message);
    this.log(`Mute set to: ${muted}`);
  }

  public setLocale(locale: string): void {
    this.state.set('locale', locale);
    const message = createMessage<SetLocalePayload>(MessageType.SET_LOCALE, { locale });
    this.sendOrQueue(message);
    this.log(`Locale set to: ${locale}`);
  }

  public setAdFree(adFree: boolean): void {
    this.state.set('adFree', adFree);
    const message = createMessage<SetAdFreePayload>(MessageType.SET_AD_FREE, { adFree });
    this.sendOrQueue(message);
    this.log(`AdFree set to: ${adFree}`);
  }

  public pause(): void {
    this.state.set('paused', true);
    const message = createMessage(MessageType.PAUSE, {});
    this.sendOrQueue(message);
    this.log('Pause command sent');
  }

  public resume(): void {
    this.state.set('paused', false);
    const message = createMessage(MessageType.RESUME, {});
    this.sendOrQueue(message);
    this.log('Resume command sent');
  }

  public async getProgress(): Promise<Record<string, unknown>> {
    try {
      const payload = await this.request<ProgressPayload>(MessageType.PROGRESS_REQUEST, MessageType.PROGRESS_RESPONSE);
      return payload.progress;
    } catch (err) {
      console.error(err);
      return {};
    }
  }

  protected onConnected(): void {
    this.log('Connected to Game!');

    this.setMuted(this.state.get('muted'));
    this.setLocale(this.state.get('locale'));
    this.setAdFree(this.state.get('adFree'));

    if (this.state.get('paused')) {
      this.pause();
    }
  }

  protected onDisconnected(): void {
    this.log('Disconnected from Game');
  }

  protected handleMessage(message: Message): void {
    if (isHandshakeMessage(message.type)) {
      this.handshake.handleMessage(message);
      return;
    }

    switch (message.type) {
      case MessageType.FRAMES_STARTED:
        this.handleFramesStarted();
        break;
      case MessageType.GAME_READY:
        this.handleGameReady();
        break;
      case MessageType.PROGRESS_SAVE:
        this.handleProgressSave(message.payload as ProgressPayload);
        break;
      case MessageType.SYNC_REQUEST:
        this.handleSyncRequest(message);
        break;
      case MessageType.PURCHASE_REQUEST:
        this.handlePurchaseRequest(message.payload as PurchaseRequestPayload);
        break;
    }
  }

  private handleFramesStarted(): void {
    this.log('FRAMES_STARTED received');
    this.hooks.onFramesStarted?.();
  }

  private handleGameReady(): void {
    this.log('GAME_READY received');
    this.hooks.onGameReady?.();
  }

  private handleProgressSave(payload: ProgressPayload): void {
    this.log(`Progress sync: ${Object.keys(payload.progress).length} keys`);
    this.hooks.onProgressSync?.(payload.progress);
  }

  private async handleSyncRequest(message: Message): Promise<void> {
    let progress: Record<string, unknown> = {};
    if (typeof this.hooks.onProgressRequest === 'function') {
      progress = await this.hooks.onProgressRequest();
    }

    const response = createMessage<SyncPayload>(
      MessageType.SYNC_RESPONSE,
      { progress, state: this.state.getAll(), syncedAt: Date.now() },
      message.id,
    );
    this.transport.send(response);
    this.log('Sent progress data');
  }

  private async handlePurchaseRequest(payload: PurchaseRequestPayload): Promise<void> {
    this.log('Purchase request:', payload);

    if (this.processedPurchases.has(payload.requestId)) {
      this.log('Purchase request already processed (idempotent), ignoring');
      return;
    }

    this.processedPurchases.add(payload.requestId);

    try {
      let result: PurchaseResultPayload;

      if (this.hooks.onPurchaseRequest) {
        result = await this.hooks.onPurchaseRequest(payload);
      } else {
        this.log('Purchase request not implemented, ignoring');
        result = { requestId: payload.requestId, status: 'CANCELLED' };
      }

      const message = createMessage(MessageType.PURCHASE_RESULT, result);
      this.transport.send(message);
      this.log('Purchase result sent:', result);
    } catch (error) {
      const result: PurchaseResultPayload = {
        requestId: payload.requestId,
        status: 'FAILED',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
      const message = createMessage(MessageType.PURCHASE_RESULT, result);
      this.transport.send(message);
      this.log('Purchase error:', error);
    }
  }
}
